<template>
  <div class="place-selection-upload-tab h-100">
    <div class="upload-tab-left">
      <b-form-group label="Choose input method">
        <b-form-radio-group
          v-model="inputMethod"
          :options="inputMethodOptions"
          buttons
          button-variant="outline-primary"
          size="sm"
          @change="onInputMethodChange"
        />
      </b-form-group>

      <div v-if="inputMethod === 'TEXT'" class="mb-3">
        <b-form-textarea
          v-model="rawText"
          rows="10"
          placeholder="Paste GeoJSON or KML here..."
          :state="parseState"
          @input="onTextInput"
        />
        <b-form-invalid-feedback>{{ parseError }}</b-form-invalid-feedback>
      </div>

      <!-- ESRI file upload -->
      <div v-if="inputMethod === 'FILE'" class="mb-3">
        <div
          class="upload-source-card"
          :class="{ 'upload-source-card--drag': isDragActive }"
          @click="openFilePicker"
          @dragenter.prevent="onDragOver"
          @dragover.prevent="onDragOver"
          @dragleave.prevent="onDragLeave"
          @drop.prevent="onFileDrop"
        >
          <input
            ref="uploadFileInput"
            type="file"
            class="d-none"
            accept=".csv,.zip,.json,.geojson,.kml"
            @change="onFileUpload"
          />

          <div class="upload-source-card__icon">
            <i class="fe fe-upload-cloud"></i>
          </div>
          <div class="upload-source-card__title">Upload file</div>
          <div class="upload-source-card__help">
            Drag and drop a file here, or click to browse. Supports CSV, GeoJSON, KML,
            or a zipped Shapefile (must include the .prj).
          </div>

          <div class="upload-source-card__actions">
            <b-button variant="outline-primary" size="sm" @click.stop="openFilePicker">
              Browse files
            </b-button>
          </div>
        <div v-if="parseError" class="text-danger mt-1">{{ parseError }}</div>
        </div>

        <!-- CSV help message + instructions toggle -->
        <div class="csv-help mt-2">
          <span class="csv-help__label">CSV</span>
          <span class="text-muted ml-1">Upload a list of places as a CSV file.</span>
          <b-button
            variant="link"
            size="sm"
            class="p-0 ml-1 align-baseline"
            @click.stop="showCsvInstructions = !showCsvInstructions"
          >
            {{ showCsvInstructions ? 'Hide instructions' : 'Show more instructions' }}
          </b-button>

          <b-collapse v-model="showCsvInstructions" class="csv-instructions mt-2">
            <div class="csv-instructions__body">
              <p class="mb-1">
                The CSV must have <strong>no header row</strong>. Provide one place per line
                with the following columns, in order:
              </p>
              <ol class="csv-instructions__cols mb-2">
                <li><strong>Name</strong> — the place name</li>
                <li><strong>Address</strong> — the full address</li>
                <li>
                  <strong>Latitude</strong> — decimal degrees, between -90 and 90
                  <em>(optional)</em>
                </li>
                <li>
                  <strong>Longitude</strong> — decimal degrees, between -180 and 180
                  <em>(optional)</em>
                </li>
              </ol>
              <p class="mb-1 text-muted small">
                Latitude and longitude are optional. Leave them blank to have the
                location derived from the address automatically.
              </p>
              <p class="mb-1">Example:</p>
              <pre class="csv-instructions__example mb-2">Name 1,Address 1 (full address),40.7828687,-73.9675491
Name 2,Address 2 (full address)</pre>
              <p class="mb-1 text-muted small">
                Wrap a value in double quotes if it contains a comma
                (e.g. <code>"123 Main St, Suite 4"</code>). Provide both latitude and
                longitude together or leave both blank. Rows missing an address, or with
                only one coordinate or out-of-range coordinates, are skipped.
              </p>
              <a :href="csvTemplateHref" download="Template_point.csv">
                Download template file (Template_point.csv)
              </a>
            </div>
          </b-collapse>
        </div>
      </div>

      <!-- Parsed feature list -->
      <div v-if="parsedFeatures.length > 0" class="feature-list">
        <div class="d-flex align-items-center justify-content-between mb-2">
          <strong>{{ parsedFeatures.length }} feature(s) found</strong>
          <b-button size="sm" variant="link" @click="toggleSelectAll">
            {{ allSelected ? 'Deselect all' : 'Select all' }}
          </b-button>
        </div>
        <div
          v-for="(feature, i) in parsedFeatures"
          :key="i"
          class="feature-row"
          :class="{ selected: selectedIndexes.includes(i) }"
          @click="toggleFeature(i)"
        >
          <b-form-checkbox
            :checked="selectedIndexes.includes(i)"
            @change="toggleFeature(i)"
            @click.native.stop
          />
          <span class="feature-name ml-2">{{ feature.properties && feature.properties.name || 'Feature ' + (i + 1) }}</span>
        </div>
      </div>

      <!-- CSV upload block -->
      <div v-if="inputMethod === 'FILE' && csv.active" class="csv-block mt-3">
        <div class="csv-block__guidance text-muted mb-2">
          CSV must have no header row; columns in order: Name, Address, latitude (optional),
          longitude (optional)
        </div>

        <b-alert v-if="csv.parsing" variant="info" show>
          Processing file...
        </b-alert>

        <div v-if="csv.error" class="text-danger mb-2">{{ csv.error }}</div>

        <template v-if="!csv.error && csv.rows.length > 0">
          <div class="csv-summary mb-2">
            <strong>{{ csv.fileName }}</strong>
            <span class="text-muted"> — {{ csv.summary.count }} valid row(s)</span>
          </div>

          <div class="csv-preview-list">
            <div
              v-for="(row, i) in csv.rows.slice(0, csvPreviewCap)"
              :key="i"
              class="csv-preview-row"
            >
              <span class="csv-preview-name">{{ row.name || 'Row ' + (i + 1) }}</span>
              <span class="csv-preview-address text-muted ml-2">{{ row.address }}</span>
            </div>
          </div>

          <div v-if="csv.truncated" class="text-muted mt-1 small">
            Showing the first {{ csvPreviewCap }} of {{ csv.summary.count }} rows.
          </div>

          <div v-if="csv.excludedCount > 0" class="text-muted mt-1 small">
            {{ csv.excludedCount }} row(s) were excluded as malformed or invalid.
          </div>

          <b-form-group label="Place type" class="mt-3 mb-2">
            <b-form-radio-group
              v-model="csv.placeType"
              :options="placeTypeOptions"
              buttons
              button-variant="outline-primary"
              size="sm"
              @change="onPlaceTypeChange"
            />
          </b-form-group>

          <template v-if="csv.placeType === 'Radius'">
            <b-form-group label="Radius unit" class="mb-2">
              <b-form-radio-group
                v-model="csv.radiusUnit"
                :options="radiusUnitOptions"
                buttons
                button-variant="outline-primary"
                size="sm"
                @change="onRadiusUnitChange"
              />
            </b-form-group>

            <b-form-group label="Radius" class="mb-2">
              <b-form-input
                v-model="csv.radiusValue"
                type="number"
                step="0.01"
                min="0.01"
                max="999999.99"
                size="sm"
                :state="csv.radiusValue === '' ? null : radiusValueValid"
                placeholder="e.g. 1.5"
                @input="onRadiusValueInput"
              />
              <b-form-invalid-feedback :state="radiusValueValid">
                {{ radiusValidationMessage }}
              </b-form-invalid-feedback>
            </b-form-group>
          </template>

          <b-form-group label="Category" class="mt-3 mb-2">
            <b-form-input
              v-model="csv.category"
              placeholder="Enter a category"
              size="sm"
            />
          </b-form-group>

          <b-form-group label="Brand" class="mb-2">
            <b-form-input
              v-model="csv.brand"
              placeholder="Enter a brand"
              size="sm"
            />
          </b-form-group>
        </template>
      </div>

      <b-alert v-if="uploadProcessing" variant="info" show class="mt-2">
        Processing file...
      </b-alert>

      <b-button
        class="mt-3"
        variant="primary"
        :disabled="addFeaturesDisabled"
        @click="onAddFeatures"
      >
        {{ addFeaturesLabel }}
      </b-button>
    </div>
  </div>
</template>

<script>
import JobService from '@/services/dataExplorer/jobService.js';
import { parseZip } from 'shpjs';
import {
  parseCSV,
  toPreviewFeatures,
  toRadiusPreviewFeatures,
  buildFileUploadForm,
  CSV_PREVIEW_CAP,
  CSV_MAX_BYTES,
} from './placeSelectionCsv';
import { isEmptyValue } from './placeSelectionSaveHelpers';

export default {
  name: 'PlaceSelectionUploadTab',
  mixins: [JobService],
  data() {
    return {
      inputMethod: 'FILE',
      inputMethodOptions: [
        { value: 'FILE', text: 'Upload file' },
        { value: 'TEXT', text: 'Paste text' },
      ],
      placeTypeOptions: [
        { value: 'Point', text: 'Point' },
        { value: 'Radius', text: 'Radius' },
      ],
      radiusUnitOptions: [
        { value: 'miles', text: 'Miles' },
        { value: 'kilometers', text: 'Kilometers' },
      ],
      rawText: '',
      parsedFeatures: [],
      selectedIndexes: [],
      parseState: null,
      parseError: '',
      uploadProcessing: false,
      showCsvInstructions: false,
      isDragActive: false,
      csvFile: null,
      csv: {
        active: false,
        fileName: '',
        parsing: false,
        uploading: false,
        rows: [],
        previewFeatures: [],
        summary: { count: 0 },
        excludedCount: 0,
        truncated: false,
        category: '',
        brand: '',
        error: '',
        placeType: 'Point',
        radiusUnit: 'miles',
        radiusValue: '',
      },
    };
  },
  created() {
    // Small local debounce (~250ms) wrapping rebuildPreviewFeatures for radius-value typing.
    // Timer id is stored on the instance so beforeDestroy can clear a pending rebuild.
    this._rebuildTimer = null;
    this.debouncedRebuild = () => {
      if (this._rebuildTimer) clearTimeout(this._rebuildTimer);
      this._rebuildTimer = setTimeout(() => {
        this._rebuildTimer = null;
        this.rebuildPreviewFeatures();
      }, 250);
    };
  },
  beforeDestroy() {
    if (this._rebuildTimer) {
      clearTimeout(this._rebuildTimer);
      this._rebuildTimer = null;
    }
  },
  computed: {
    allSelected() {
      return this.selectedIndexes.length === this.parsedFeatures.length && this.parsedFeatures.length > 0;
    },
    csvPreviewCap() {
      return CSV_PREVIEW_CAP;
    },
    csvTemplateHref() {
      return `${process.env.BASE_URL || '/'}Template_point.csv`;
    },
    canAddCsv() {
      return (
        this.csv.active
        && !this.csv.error
        && this.csv.rows.length > 0
        && !!this.csv.category.trim()
        && !!this.csv.brand.trim()
        && !this.csv.uploading
      );
    },
    radiusValueValid() {
      // Req 3.3 / 3.7: finite, 0.01..999999.99 inclusive, at most 2 decimals.
      const raw = (this.csv.radiusValue ?? '').toString().trim();
      if (raw === '') return false;
      if (!/^\d+(\.\d{1,2})?$/.test(raw)) return false; // numeric, <=2 decimals, no sign/exp
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0.01 && n <= 999999.99;
    },
    radiusDecimalsInvalid() {
      // Distinguishes the "too many decimals" message (Req 3.7) from range/empty (Req 3.4).
      const raw = (this.csv.radiusValue ?? '').toString().trim();
      return /^\d+\.\d{3,}$/.test(raw);
    },
    showRadiusControls() {
      return this.inputMethod === 'FILE' && this.csv.active && this.csv.placeType === 'Radius';
    },
    radiusValidationMessage() {
      // Req 3.7: too-many-decimals message; Req 3.4: range/empty message.
      if (this.radiusDecimalsInvalid) {
        return 'Enter a radius with at most two decimal places.';
      }
      return 'Enter a positive decimal radius between 0.01 and 999999.99.';
    },
    // The CSV flow reuses the shared "Add feature(s)" button instead of a
    // separate "Add Places" button.
    isCsvMode() {
      return this.inputMethod === 'FILE' && this.csv.active && !this.csv.error;
    },
    addFeaturesCount() {
      return this.isCsvMode ? this.csv.summary.count : this.selectedIndexes.length;
    },
    addFeaturesLabel() {
      if (this.isCsvMode && this.csv.uploading) {
        return 'Uploading...';
      }
      return `Add feature(s) (${this.addFeaturesCount})`;
    },
    addFeaturesDisabled() {
      return this.isCsvMode ? !this.canAddCsv : this.selectedIndexes.length === 0;
    },
  },
  methods: {
    rebuildPreviewFeatures() {
      const rows = this.csv.rows || [];
      let features;
      if (this.csv.placeType === 'Radius' && this.radiusValueValid) {
        features = toRadiusPreviewFeatures(rows, Number(this.csv.radiusValue), this.csv.radiusUnit);
      } else {
        features = toPreviewFeatures(rows);
      }
      this.csv.previewFeatures = features;
      this.$emit('preview-features-changed', features);
    },
    onPlaceTypeChange() {
      // Req 5.1 / 5.2: Point<->Radius toggle rebuilds immediately.
      this.rebuildPreviewFeatures();
    },
    onRadiusUnitChange() {
      // Req 2.4 / 2.5 / 5.3: unit change rebuilds immediately; with no valid
      // radius the 5.1 fallback keeps existing Point features and the unit is retained.
      this.rebuildPreviewFeatures();
    },
    onRadiusValueInput() {
      // Req 5.3: debounce rapid typing into a single rebuild (~250ms, well within Req 2.4's 2s).
      this.debouncedRebuild();
    },
    emitPreviewFeatures() {
      const features = this.selectedIndexes
        .map((i) => this.parsedFeatures[i])
        .filter(Boolean);
      this.$emit('preview-features-changed', features);
    },
    resetParsedState() {
      this.rawText = '';
      this.parsedFeatures = [];
      this.selectedIndexes = [];
      this.parseState = null;
      this.parseError = '';
      this.emitPreviewFeatures();
    },
    onInputMethodChange() {
      this.resetParsedState();
      this.resetCsvState();
    },
    resetCsvState() {
      this.csvFile = null;
      this.csv.active = false;
      this.csv.fileName = '';
      this.csv.parsing = false;
      this.csv.uploading = false;
      this.csv.rows = [];
      this.csv.previewFeatures = [];
      this.csv.summary = { count: 0 };
      this.csv.excludedCount = 0;
      this.csv.truncated = false;
      this.csv.category = '';
      this.csv.brand = '';
      this.csv.error = '';
      // Req 1.5 / 2.3 / 2.6: clearing the CSV (or an input-method change, which
      // routes through here) resets Place Type to Point and radius fields to defaults.
      this.csv.placeType = 'Point';
      this.csv.radiusUnit = 'miles';
      this.csv.radiusValue = '';
      this.$emit('preview-features-changed', []);
    },
    async handleCsvFile(file) {
      this.csvFile = file;
      if (file.size > CSV_MAX_BYTES) {
        this.csv.active = true;
        this.csv.fileName = file.name || '';
        this.csv.rows = [];
        this.csv.previewFeatures = [];
        this.csv.summary = { count: 0 };
        this.csv.excludedCount = 0;
        this.csv.truncated = false;
        this.csv.error = 'CSV file is too large. Maximum size is 5 MB.';
        this.$emit('preview-features-changed', []);
        return;
      }
      this.csv.parsing = true;
      this.csv.error = '';
      try {
        const text = await file.text();
        const parseResult = parseCSV(text);
        const valid = parseResult.valid || [];
        this.csv.rows = valid;
        this.csv.summary = { count: parseResult.validCount };
        this.csv.excludedCount = parseResult.excludedCount;
        this.csv.fileName = file.name || '';
        this.csv.truncated = parseResult.validCount > CSV_PREVIEW_CAP;
        // Req 1.2: a newly active CSV defaults to Point; rebuild is the single
        // source of truth for csv.previewFeatures and the preview emit.
        this.csv.placeType = 'Point';
        this.csv.active = true;
        this.rebuildPreviewFeatures();
        if (parseResult.error) {
          this.csv.error = parseResult.error;
        }
      } finally {
        this.csv.parsing = false;
      }
    },
    openFilePicker() {
      this.$refs.uploadFileInput?.click();
    },
    detectTextFormat(text) {
      const trimmed = text.trim();
      if (!trimmed) {
        return null;
      }
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        return 'GEOJSON';
      }
      if (trimmed.startsWith('<')) {
        return 'KML';
      }
      return null;
    },
    parseTextInput() {
      const detectedFormat = this.detectTextFormat(this.rawText);
      if (detectedFormat === 'GEOJSON') {
        this.parseGeoJSON();
        return;
      }
      if (detectedFormat === 'KML') {
        this.parseKML();
        return;
      }
      this.parseState = false;
      this.parseError = 'Unable to detect whether the pasted text is GeoJSON or KML.';
      this.parsedFeatures = [];
      this.selectedIndexes = [];
      this.emitPreviewFeatures();
    },
    onTextInput() {
      if (this.inputMethod !== 'TEXT') {
        return;
      }
      if (!this.rawText.trim()) {
        this.resetParsedState();
        return;
      }
      this.parseTextInput();
    },
    parseGeoJSON() {
      try {
        let geojson = JSON.parse(this.rawText);
        geojson = this.fixGeoJSON(geojson);
        const features = geojson.type === 'FeatureCollection'
          ? geojson.features
          : geojson.type === 'Feature' ? [geojson] : [];
        if (features.length === 0) {
          this.parseState = false;
          this.parseError = 'No features found in GeoJSON.';
          return;
        }
        this.parsedFeatures = features;
        this.selectedIndexes = features.map((_, i) => i);
        this.parseState = true;
        this.parseError = '';
        this.emitPreviewFeatures();
      } catch {
        this.parseState = false;
        this.parseError = 'Invalid GeoJSON. Please check your input.';
        this.parsedFeatures = [];
        this.selectedIndexes = [];
        this.emitPreviewFeatures();
      }
    },
    parseKML() {
      try {
        const dom = new DOMParser().parseFromString(this.rawText, 'text/xml');
        let geojson = toGeoJSON.kml(dom);
        geojson = this.fixGeoJSON(geojson);
        const features = geojson.features || [];
        if (features.length === 0) {
          this.parseState = false;
          this.parseError = 'No features found in KML.';
          return;
        }
        this.parsedFeatures = features;
        this.selectedIndexes = features.map((_, i) => i);
        this.parseState = true;
        this.parseError = '';
        this.emitPreviewFeatures();
      } catch {
        this.parseState = false;
        this.parseError = 'Invalid KML. Please check your input.';
        this.parsedFeatures = [];
        this.selectedIndexes = [];
        this.emitPreviewFeatures();
      }
    },
    async onFileUpload(event) {
      const file = event.target.files[0];
      // Reset the input so selecting the same file again re-triggers change.
      if (event.target) event.target.value = '';
      await this.processFile(file);
    },
    onDragOver() {
      if (this.inputMethod !== 'FILE') return;
      this.isDragActive = true;
    },
    onDragLeave() {
      this.isDragActive = false;
    },
    async onFileDrop(event) {
      this.isDragActive = false;
      if (this.inputMethod !== 'FILE') return;
      const file = event.dataTransfer
        && event.dataTransfer.files
        && event.dataTransfer.files[0];
      await this.processFile(file);
    },
    async processFile(file) {
      if (!file) return;
      const lowerName = (file.name || '').toLowerCase();
      if (lowerName.endsWith('.csv')) {
        return this.handleCsvFile(file);
      }
      this.uploadProcessing = true;
      this.parseError = '';
      this.parsedFeatures = [];
      this.selectedIndexes = [];
      try {
        const isZip = lowerName.endsWith('.zip');
        const isGeoJson = lowerName.endsWith('.json') || lowerName.endsWith('.geojson');
        const isKml = lowerName.endsWith('.kml');
        let geojson;
        if (isGeoJson) {
          const text = await file.text();
          this.rawText = text;
          this.parseState = null;
          this.parseError = '';
          this.parseGeoJSON();
          return;
        }
        if (isKml) {
          const text = await file.text();
          this.rawText = text;
          this.parseState = null;
          this.parseError = '';
          this.parseKML();
          return;
        }
        if (isZip || file.size > 2 * 1024 * 1024) {
          //const { parseZip } = await import('@mapbox/togeojson');
          const buffer = await file.arrayBuffer();
          geojson = await parseZip(buffer);
        } else {
          const config = this.getHeaders();
          const formData = new FormData();
          formData.append('file', file);
          config.headers['Content-Type'] = 'multipart/form-data';
          const response = await this.$http.post('v2/places/esri/convert-to-geojson', formData, config);
          geojson = response?.data?.payload;
        }
        if (!geojson || geojson.error) {
          this.parseError = geojson?.error?.response?.data?.longMessage || 'Error parsing ESRI Shapefile.';
          return;
        }
        geojson = this.fixGeoJSON(geojson);
        this.parsedFeatures = geojson.features || [];
        this.selectedIndexes = this.parsedFeatures.map((_, i) => i);
        this.emitPreviewFeatures();
      } catch (e) {
        this.parseError = 'Error processing file. Please ensure it is a valid GeoJSON, KML, or ESRI Shapefile ZIP.';
        console.error(e);
        this.emitPreviewFeatures();
      } finally {
        this.uploadProcessing = false;
      }
    },
    toggleFeature(index) {
      const pos = this.selectedIndexes.indexOf(index);
      if (pos === -1) {
        this.selectedIndexes.push(index);
      } else {
        this.selectedIndexes.splice(pos, 1);
      }
      this.emitPreviewFeatures();
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedIndexes = [];
      } else {
        this.selectedIndexes = this.parsedFeatures.map((_, i) => i);
      }
      this.emitPreviewFeatures();
    },
    onAddFeatures() {
      // The CSV upload flow shares the "Add feature(s)" button with the
      // GeoJSON/KML/Shapefile flow but has its own add path.
      if (this.isCsvMode) {
        return this.addCsvPlaces();
      }
      return this.addSelected();
    },
    addSelected() {
      const toAdd = this.selectedIndexes.map((i) => {
        const f = this.parsedFeatures[i];
        return {
          _source: 'upload',
          name: f.properties?.name || `Uploaded Feature ${i + 1}`,
          geometry: f.geometry,
          fileName: this.uploadFile ? this.uploadFile.name : null,
        };
      });
      this.$sessionStore.commit('addSelectedPlaces', toAdd);
      // Reset after adding
      this.inputMethod = 'FILE';
      this.resetParsedState();
    },
    getCompanyId() {
      return (
        this.$persistingStore.state.user.selected_company_id
        || this.$persistingStore.state.user.defaultCompanyId
        || null
      );
    },
    async addCsvPlaces() {
      if (!this.canAddCsv) return;
      this.csv.uploading = true;
      this.csv.error = '';
      try {
        const config = this.getHeaders();
        config.headers['Content-Type'] = 'multipart/form-data';
        const form = buildFileUploadForm(this.csvFile);
        form.append('companyId', this.getCompanyId());
        const res = await this.$http.post('v2/places/upload/custom-file', form, config);
        const payload = res && res.data ? res.data.payload : null;
        if (!payload || !payload.uploadPath || !payload.fileName) {
          this.csv.error = 'Upload failed. Please try again.';
          return;
        }
        const isRadius = this.csv.placeType === 'Radius' && this.radiusValueValid;
        // hasAddress: true only when every parsed CSV row carries a non-empty
        // address. Consumed by validateSaveablePlaces to evaluate a CSV_Place's
        // address at the file-row level. Basing this on the parsed rows (rather
        // than the map preview features) is required now that lat/lon are
        // optional: rows without a coordinate are geocoded from their address
        // server-side and are intentionally omitted from previewFeatures, so a
        // preview-based check would wrongly report no address for such files.
        // Defaults to false when there are no rows so save validation fails safe.
        const rows = this.csv.rows || [];
        const hasAddress = rows.length > 0
          && rows.every((r) => !isEmptyValue(r && r.address));
        this.$sessionStore.commit('addSelectedPlaces', [{
          _source: 'csv',
          name: this.csv.fileName,
          fileName: this.csv.fileName,
          csvSummary: { count: this.csv.summary.count, hasAddress },
          previewFeatures: this.csv.previewFeatures,
          category: this.csv.category.trim(),
          brand: this.csv.brand.trim(),
          uploadPath: payload.uploadPath,
          uploadFileName: payload.fileName,
          // Radius metadata (only meaningful for a Radius upload). Carried
          // through so the library registration can forward a radius; the
          // backend normalizes the unit and converts km -> miles.
          placeType: this.csv.placeType,
          radiusValue: isRadius ? Number(this.csv.radiusValue) : null,
          radiusUnit: isRadius ? this.csv.radiusUnit : null,
        }]);
        this.resetCsvState();
      } catch (e) {
        console.error(e);
        this.csv.error = 'Upload failed. Please try again.';
      } finally {
        this.csv.uploading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.place-selection-upload-tab {
  height: 100%;
}

.upload-tab-left {
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
}

.place-selection-upload-tab ::v-deep .form-group > label {
  font-weight: 600;
}

.upload-source-card {
  border: 1px dashed #c8d3e6;
  border-radius: 8px;
  padding: 2rem 1.5rem;
  background: #f9fbfd;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &:hover {
    border-color: #95aac9;
    background: #f3f6fb;
  }

  &--drag {
    border-color: #4f14dc;
    border-style: solid;
    background: #f1ecfd;
  }

  &__icon {
    font-size: 2.5rem;
    color: #95aac9;
    margin-bottom: 0.5rem;
  }

  &__title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #12263f;
  }

  &__help {
    margin-top: 0.25rem;
    color: #6e84a3;
    font-size: 0.8rem;
  }

  &__actions {
    margin-top: 0.75rem;
    display: flex;
    justify-content: center;
  }
}

.csv-help {
  font-size: 0.8rem;

  &__label {
    display: inline-block;
    font-weight: 600;
    font-size: 0.7rem;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #10b981;
    border: 1px solid #10b981;
    border-radius: 4px;
    padding: 0 0.35rem;
  }
}

.csv-instructions {
  &__body {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    background: #f9fbfd;
    padding: 0.75rem;
    font-size: 0.8rem;
  }

  &__cols {
    padding-left: 1.1rem;
    margin-bottom: 0.5rem;
  }

  &__example {
    background: #eef2f7;
    border-radius: 4px;
    padding: 0.5rem;
    font-size: 0.75rem;
    white-space: pre-wrap;
    word-break: break-word;
    margin: 0;
  }
}

.csv-block {
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;

  &__guidance {
    font-size: 0.8rem;
  }

  .csv-preview-list {
    max-height: 220px;
    overflow-y: auto;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
    padding: 0.5rem;

    .csv-preview-row {
      display: flex;
      align-items: center;
      padding: 0.25rem 0.5rem;
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.feature-list {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 0.5rem;

  .feature-row {
    display: flex;
    align-items: center;
    padding: 0.35rem 0.5rem;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background: #f8fafc;
    }

    &.selected {
      background: #eff6ff;
    }

    .feature-name {
      font-size: 0.875rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}
</style>
